import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import {
  type DriverAggregationTripsAndStopsRequest,
  type DriverDailyAggregationTripsAndStopsRequest,
  type EventsTripsAndStopsRequest,
  type TripsAndStopsEmailReportRequest,
  type TripsAndStopsReportRequest,
  TripsAndStopsRequester,
  type TripsAndStopsResponse,
  type VehicleAggregationTripsAndStopsRequest,
  type VehicleDailyAggregationTripsAndStopsRequest,
} from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const requester = new TripsAndStopsRequester({ host: env.apiHost });

const QUERY_KEY = 'TRIP_AND_STOPS';

const getNextPageParam = (
  lastPage: TripsAndStopsResponse,
  allPages: TripsAndStopsResponse[]
) => {
  const loadedItems = allPages
    .flat()
    .map((response) => response.rows.length)
    .reduce((acc, a) => acc + a);

  if (lastPage.count === loadedItems) return undefined;
  return allPages.flat().length;
};

const useGetVehicleAggregation = (
  params: VehicleAggregationTripsAndStopsRequest
) =>
  useQuery<TripsAndStopsResponse>({
    queryKey: [QUERY_KEY, 'VEHICLE', JSON.stringify(params)],
    queryFn: () => requester.getVehicleAggregation(params),
  });

const useGetDriverAggregation = (
  params: DriverAggregationTripsAndStopsRequest
) =>
  useQuery<TripsAndStopsResponse>({
    queryKey: [QUERY_KEY, 'DRIVER', JSON.stringify(params)],
    queryFn: () => requester.getDriverAggregation(params),
  });

const useGetDailyVehicleAggregation = (
  params: VehicleDailyAggregationTripsAndStopsRequest
) => {
  return useInfiniteQuery<TripsAndStopsResponse>({
    queryKey: [QUERY_KEY, 'DAILY_VEHICLE', JSON.stringify(params)],
    queryFn: ({ pageParam = 0 }) =>
      requester.getDailyVehicleAggregation({
        ...params,
        page: pageParam,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useGetDailyDriverAggregation = (
  params: DriverDailyAggregationTripsAndStopsRequest
) => {
  return useInfiniteQuery<TripsAndStopsResponse>({
    queryKey: [QUERY_KEY, 'DAILY_DRIVER', JSON.stringify(params)],
    queryFn: ({ pageParam = 0 }) =>
      requester.getDailyDriverAggregation({ ...params, page: pageParam }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useGetEvents = (params: EventsTripsAndStopsRequest) => {
  return useInfiniteQuery<TripsAndStopsResponse>({
    queryKey: [QUERY_KEY, 'EVENTS', JSON.stringify(params)],
    queryFn: ({ pageParam = 0 }) =>
      requester.getTripsAndStopsEvents({ ...params, page: pageParam }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useDownloadReport = () =>
  useMutation<Blob, unknown, { params: TripsAndStopsReportRequest }>({
    mutationFn: (variables) =>
      requester.downloadTripsAndStopsReport(variables.params),
  });

const useRequestReport = () =>
  useMutation<string, unknown, { params: TripsAndStopsEmailReportRequest }>({
    mutationFn: (variables) =>
      requester.requestTripsAndStopsReport(variables.params),
  });

export {
  useGetVehicleAggregation,
  useGetDriverAggregation,
  useGetDailyVehicleAggregation,
  useGetDailyDriverAggregation,
  useGetEvents,
  useDownloadReport,
  useRequestReport,
};
